import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CalendarIcon, MapPinIcon, ClockIcon, UsersIcon } from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const EventDetailPage = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/events/${id}`);
        
        if (response.ok) {
          const data = await response.json();
          setEvent(data);
        } else {
          // Fallback to mock data if API fails
          const mockEvent = {
            id: id,
            title: 'Annual Sports Banquet',
            description: `Join us for our annual sports banquet celebrating the achievements of all our student-athletes. This special evening will feature dinner, awards presentations, and recognition of our outstanding teams and individual performers.\n\nThe event will include:\n- Welcome reception at 6:00 PM\n- Dinner service at 6:30 PM\n- Awards ceremony at 7:30 PM\n- Team photo sessions\n\nDress code: Semi-formal attire\n\nTickets are required and can be purchased at the main office or online. Seating is limited, so please reserve your spots early.`,
            date: '2024-02-15T18:00:00Z',
            endDate: '2024-02-15T21:00:00Z',
            location: 'East End Prep Gymnasium',
            address: '123 School Street, East End, NY 12345',
            category: 'Awards Ceremony',
            organizer: 'Athletics Department',
            capacity: 200,
            registered: 145,
            price: '$25 per person',
            image: '/images/events/sports-banquet.jpg',
            contact: {
              name: 'Sarah Johnson',
              email: 'athletics@eastendprep.edu',
              phone: '(555) 123-4567'
            }
          };
          setEvent(mockEvent);
        }
      } catch (error) {
        console.error('Error fetching event:', error);
        // Fallback to mock data on error
        const mockEvent = {
          id: id,
          title: 'Annual Sports Banquet',
          description: `Join us for our annual sports banquet celebrating the achievements of all our student-athletes. This special evening will feature dinner, awards presentations, and recognition of our outstanding teams and individual performers.\n\nThe event will include:\n- Welcome reception at 6:00 PM\n- Dinner service at 6:30 PM\n- Awards ceremony at 7:30 PM\n- Team photo sessions\n\nDress code: Semi-formal attire\n\nTickets are required and can be purchased at the main office or online. Seating is limited, so please reserve your spots early.`,
          date: '2024-02-15T18:00:00Z',
          endDate: '2024-02-15T21:00:00Z',
          location: 'East End Prep Gymnasium',
          address: '123 School Street, East End, NY 12345',
          category: 'Awards Ceremony',
          organizer: 'Athletics Department',
          capacity: 200,
          registered: 145,
          price: '$25 per person',
          image: '/images/events/sports-banquet.jpg',
          contact: {
            name: 'Sarah Johnson',
            email: 'athletics@eastendprep.edu',
            phone: '(555) 123-4567'
          }
        };
        setEvent(mockEvent);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Event Not Found</h1>
          <p className="text-gray-600">The event you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        {event.image && (
          <img
            src={event.image}
            alt={event.title}
            className="w-full h-64 object-cover"
          />
        )}
        
        <div className="p-8">
          <div className="mb-6">
            <span className="inline-block bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full mb-4">
              {event.eventType || event.category || 'Event'}
            </span>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{event.title}</h1>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-600">
              <div className="flex items-center">
                <CalendarIcon className="h-5 w-5 mr-2" />
                <span>{formatDate(event.startDate || event.date)}</span>
              </div>
              <div className="flex items-center">
                <ClockIcon className="h-5 w-5 mr-2" />
                <span>{formatTime(event.startDate || event.date)}{event.endDate && ` - ${formatTime(event.endDate)}`}</span>
              </div>
              <div className="flex items-center">
                <MapPinIcon className="h-5 w-5 mr-2" />
                <span>{event.location || 'Location TBD'}</span>
              </div>
              {(event.registered !== undefined && event.capacity !== undefined) && (
                <div className="flex items-center">
                  <UsersIcon className="h-5 w-5 mr-2" />
                  <span>{event.registered}/{event.capacity} registered</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="prose max-w-none mb-8">
            {event.description && event.description.split('\n').map((paragraph, index) => (
              <p key={index} className="mb-4 text-gray-700 leading-relaxed">
                {paragraph}
              </p>
            ))}
            {!event.description && (
              <p className="mb-4 text-gray-700 leading-relaxed">
                No description available.
              </p>
            )}
          </div>
          
          <div className="border-t border-gray-200 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Event Details</h3>
                <dl className="space-y-2">
                  {event.organizer && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Organizer</dt>
                      <dd className="text-sm text-gray-900">{event.organizer}</dd>
                    </div>
                  )}
                  {event.price && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Price</dt>
                      <dd className="text-sm text-gray-900">{event.price}</dd>
                    </div>
                  )}
                  {event.address && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Address</dt>
                      <dd className="text-sm text-gray-900">{event.address}</dd>
                    </div>
                  )}
                </dl>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact Information</h3>
                <dl className="space-y-2">
                  {event.contact?.name && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Contact Person</dt>
                      <dd className="text-sm text-gray-900">{event.contact.name}</dd>
                    </div>
                  )}
                  {event.contact?.email && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Email</dt>
                      <dd className="text-sm text-gray-900">
                        <a href={`mailto:${event.contact.email}`} className="text-blue-600 hover:text-blue-800">
                          {event.contact.email}
                        </a>
                      </dd>
                    </div>
                  )}
                  {event.contact?.phone && (
                     <div>
                       <dt className="text-sm font-medium text-gray-500">Phone</dt>
                       <dd className="text-sm text-gray-900">
                         <a href={`tel:${event.contact.phone}`} className="text-blue-600 hover:text-blue-800">
                           {event.contact.phone}
                         </a>
                       </dd>
                     </div>
                   )}
                </dl>
              </div>
            </div>
            
            <div className="mt-6 flex flex-col sm:flex-row gap-4">
              <button className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors">
                Register for Event
              </button>
              <button className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors">
                Add to Calendar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailPage;